import Header from "@/components/Header";
import Catalog from "@/components/Catalog";
import { Wrench, Truck, ShieldCheck, Phone, Mail, MapPin, ArrowDown, Clock } from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="home-page">
      <Header />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-12 md:py-16">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-700">Vendita &amp; Riparazione · Bagnolo San Vito (MN)</p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight mt-4">
              Morselli Renzo Macchine da Cucire: il punto di riferimento per il tuo cucito
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-6 max-w-xl">
              Esplora la nostra selezione di macchine da cucire domestiche, industriali e ricamatrici.
              Seleziona il modello ideale e invia una richiesta d'ordine direttamente in negozio: ti ricontattiamo noi, senza alcun impegno.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <a href="#catalogo" data-testid="hero-cta-catalog"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white rounded-full px-7 py-3.5 text-sm font-semibold transition-colors duration-200 shadow-[0_8px_24px_rgba(2,132,199,0.25)]">
                Esplora il Catalogo <ArrowDown className="w-4 h-4" />
              </a>
              <a href="#contatti" data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-200 hover:border-sky-300 hover:text-sky-700 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors duration-200">
                Richiedi Consulenza
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-sky-100 shadow-[0_24px_60px_rgba(2,132,199,0.12)]">
                <img
                  src="https://images.unsplash.com/photo-1759960034333-298883b34d3d?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Laboratorio di macchine da cucire" className="w-full aspect-[4/3] object-cover" data-testid="hero-image" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-xl border border-sky-100 rounded-2xl shadow-lg px-5 py-4 hidden sm:block" data-testid="hero-badge">
                <p className="text-xs uppercase tracking-[0.15em] text-sky-700 font-medium">Vendita &amp; riparazione dal 1938</p>
                <p className="font-serif text-lg font-semibold text-slate-900 mt-0.5">Tradizione della famiglia Morselli</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Catalog />

      <section id="servizi" className="bg-sky-50/60 border-y border-sky-100" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-700">Servizi &amp; assistenza</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 mt-2">
            Molto più di una vendita
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: Wrench, title: "Riparazione e Manutenzione", text: "Laboratorio interno per riparazione, taratura e manutenzione di macchine da cucire di ogni marca, anche d'epoca." },
              { icon: ShieldCheck, title: "Macchine Controllate", text: "Ogni macchina venduta viene controllata e provata da noi prima della consegna, per partire senza sorprese." },
              { icon: Truck, title: "Consegna o Ritiro in Negozio", text: "Concordiamo insieme la consegna oppure il ritiro presso il nostro negozio di Bagnolo San Vito." },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-7 shadow-[0_4px_20px_rgba(2,132,199,0.04)]" data-testid={`service-card-${i}`}>
                <span className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
                  <s.icon className="w-5 h-5" />
                </span>
                <h3 className="font-sans font-semibold text-lg text-slate-900 mt-4">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="chisiamo" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="about-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-slate-200/80">
              <img src="/negozio.jpg"
                alt="Il negozio Morselli Renzo a Bagnolo San Vito" className="w-full aspect-[4/3] object-cover" data-testid="about-image" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-700">Chi siamo</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 mt-2">
              Un'attività di famiglia, dal 1938
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-5">
              Dal 1938 la famiglia Morselli ha iniziato la sua attività di vendita e riparazione delle macchine da cucire.
              Da quasi un secolo, generazione dopo generazione, aiutiamo sartorie, laboratori artigiani e appassionati
              a scegliere e mantenere la macchina giusta.
            </p>
            <p className="text-base text-slate-600 leading-relaxed mt-4">
              Ogni macchina che vedi in catalogo è fisicamente presente nel nostro negozio di Bagnolo San Vito:
              la controlliamo, la fotografiamo e la descriviamo noi stessi. Per questo preferiamo il contatto diretto:
              quando invii una richiesta, a risponderti è chi conosce quella macchina una vite alla volta.
            </p>
          </div>
        </div>
      </section>

      <section id="dove-trovarci" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="map-section">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-700">Dove trovarci</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 mt-2">
          Il nostro negozio a Bagnolo San Vito
        </h2>
        <p className="text-base text-slate-600 mt-3 max-w-2xl">
          Via Alcide De Gasperi 1, Bagnolo San Vito (MN). Ti consigliamo di telefonare prima di passare: 0376 414128.
        </p>
        <div className="mt-8 rounded-3xl overflow-hidden border border-sky-100 shadow-[0_12px_40px_rgba(2,132,199,0.08)]">
          <iframe
            title="Mappa del negozio Morselli Renzo - Via Alcide De Gasperi 1, Bagnolo San Vito (MN)"
            src="https://www.google.com/maps?q=Via+Alcide+De+Gasperi+1,+46031+Bagnolo+San+Vito+MN,+Italia&output=embed"
            className="w-full h-[380px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="store-map"
          />
        </div>
      </section>

      <footer id="contatti" className="bg-slate-900 text-slate-300" data-testid="footer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <p className="font-serif text-2xl font-semibold text-white">Morselli Renzo</p>
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 font-medium mt-1">Macchine da Cucire</p>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                Vendita e riparazione di macchine da cucire domestiche, industriali e ricamatrici. Attività di famiglia dal 1938.
              </p>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 font-medium">Contatti</p>
              <p className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-sky-400" /> 0376 414128</p>
              <p className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-sky-400" /> morselli.r@libero.it</p>
              <p className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-sky-400" /> Via Alcide De Gasperi 1, Bagnolo San Vito (MN)</p>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 font-medium">Orari di apertura</p>
              <p className="flex items-start gap-2.5"><Clock className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" /> Lunedì – Venerdì: mattina e pomeriggio</p>
              <p className="flex items-start gap-2.5"><Clock className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" /> Sabato: solo mattina</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Si consiglia di telefonare prima di passare, per essere sicuri di trovare qualcuno in negozio.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-10 pt-6 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© 2026 Morselli Renzo · Macchine da Cucire · Bagnolo San Vito (MN)</span>
            <span>Nessun pagamento online: ogni ordine viene confermato personalmente.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
